import { Badge } from '@sgme/ui';
import { FormOrientationContext } from 'components/common-form/contexts/FormOrientationContext';
import PrecomPrice from 'components/PostTrade/TradeAllocation/components/precom-price';
import { FormattedMessage } from 'components/shared/Intl';
import { format, parse, parseISO } from 'date-fns';
import { useSPIntl } from 'hooks/useIntl';
import { IntlKey } from 'locales';
import { FC, useMemo } from 'react';
import { Card, CardBody, CardTitle } from 'react-bootstrap';
import { PreComStatus } from 'state/tradeAllocationForm';

import { Currency } from './components/Currency';
import { LabelledTextField } from './components/LabelledTextField';
import { PercentWithUnitField } from './components/PercentWithUnitField';
import { SalesCounterpart } from './components/SalesCounterpart';
import { TradeAllocation } from './components/TradeAllocation';
import { TradeAllocationNotional } from './components/TradeAllocationNotional';

import './style.scss';
import type { TradeAllocationMainFormProps } from './TradeAllocationMainForm.connect';
import { connectTradeAllocationMainForm } from './TradeAllocationMainForm.connect';

const getPrecomStatusLabel = (status: PreComStatus | undefined) => {
  switch (status) {
    case 'OPEN':
      return <FormattedMessage id="tradeAllocation.monetarySchedule.preComStatus.open" defaultMessage="Open" />;
    case 'CLOSED':
      return <FormattedMessage id="tradeAllocation.monetarySchedule.preComStatus.closed" defaultMessage="Closed" />;
    case 'NOT_STARTED':
      return (
        <FormattedMessage id="tradeAllocation.monetarySchedule.preComStatus.notStarted" defaultMessage="Not Started" />
      );
    default:
      return <FormattedMessage id="tradeAllocation.monetarySchedule.preComStatus.unknown" defaultMessage="Unknown" />;
  }
};

const getPrecomStatusColor = (status: PreComStatus | undefined) => {
  switch (status) {
    case 'OPEN':
      return 'bg-success';
    case 'CLOSED':
      return 'bg-danger';
    case 'NOT_STARTED':
      return 'bg-warning';
    default:
      return 'bg-secondary';
  }
};

const Component: FC<TradeAllocationMainFormProps> = ({
  currency,
  notional,
  tradeId,
  xMargin,
  offerPrice,
  tradingPrice,
  upfrontFee,
  product,
  wrapper,
  fileCode,
  isin,
  selectedOnBehalfClient,
  tabId,
  isReadOnly,
  isLoading,
  onNotionalChange,
  onXMarginChange,
  onOfferPriceChange,
  onTradingPriceChange,
  onUpfrontFeeChange,
  onSelectedOnBehalfClientChange,
  onF2YChange,
  onSponsoChange,
  onSponsoPercentUpFrontChange,
  onTopUpPercentUpFrontChange,
  f2Y,
  sponso,
  sponsoPercentUpFront,
  topUpPercentUpFront,
  tradeWay,
  monetarySchedule,
}) => {
  const { formatMessage, formatDisplayedDate } = useSPIntl();

  const {
    hasMonetaryPreCom,
    preComPrice,
    preComActualizedOfferPrice,
    preComCurrency,
    preComEndDate,
    preComLimitDate,
    preComReferenceRate,
    preComStartDate,
    preComStatus,
    quoteStatus,
    traderGroup,
  } = monetarySchedule || {};

  const formattedPreComLimitDate = useMemo(() => {
    if (!preComLimitDate) {
      return undefined;
    }

    const parsedDateFromIso = parseISO(preComLimitDate);
    const parsedDate = Number.isNaN(parsedDateFromIso.getTime())
      ? parse(preComLimitDate, 'yyyy-MM-dd HH:mm:ss', new Date())
      : parsedDateFromIso;

    if (Number.isNaN(parsedDate.getTime())) {
      return preComLimitDate;
    }

    return format(parsedDate, 'd MMM yyyy - h:mm:ss a');
  }, [preComLimitDate]);

  const formattedPreComStartDate = useMemo(() => {
    if (!preComStartDate) {
      return undefined;
    }

    return formatDisplayedDate(preComStartDate);
  }, [preComStartDate, formatDisplayedDate]);

  const formattedPreComEndDate = useMemo(() => {
    if (!preComEndDate) {
      return undefined;
    }

    return formatDisplayedDate(preComEndDate);
  }, [preComEndDate, formatDisplayedDate]);

  const tradeSide = useMemo(
    () => (isLoading ? undefined : formatMessage({ id: `tradeAllocation.tradeWay.${tradeWay}` as IntlKey })),
    [isLoading, formatMessage, tradeWay],
  );

  return (
    <FormOrientationContext.Provider value="vertical">
      <div
        className="trade-allocation-form main-form h-auto standard-side-panel container-fluid d-flex flex-column py-2 bg-lvl3"
        data-e2e="tradeAllocation-form"
      >
        <div className="row py-1">
          <div className={`${hasMonetaryPreCom ? 'col-8' : 'col-12'} px-1`}>
            <Card className="h-100">
              <CardBody className="py-3">
                <CardTitle as="h6" data-e2e="tab-type">
                  <FormattedMessage id="tradeAllocation.edit" />
                </CardTitle>
                <div className="row row-cols-4">
                  <LabelledTextField
                    id="tradeId"
                    labelKey="tradeAllocation.edit.tradeId"
                    value={tradeId}
                    isDisabled={isReadOnly}
                    isLoading={isLoading}
                  />
                  <LabelledTextField
                    id="fileCode"
                    labelKey="tradeAllocation.edit.fileCode"
                    value={fileCode}
                    isDisabled={isReadOnly}
                    isLoading={isLoading}
                  />
                  <LabelledTextField
                    id="isin"
                    labelKey="tradeAllocation.edit.isinCode"
                    value={isin}
                    isDisabled={isReadOnly}
                    isLoading={isLoading}
                  />
                  <SalesCounterpart
                    selectedOnBehalfClient={selectedOnBehalfClient}
                    onChange={onSelectedOnBehalfClientChange}
                    isDisabled={isReadOnly}
                    isLoading={isLoading}
                  />
                  <LabelledTextField
                    id="product"
                    labelKey="tradeAllocation.edit.product"
                    value={product}
                    isDisabled={isReadOnly}
                    isLoading={isLoading}
                  />
                  <LabelledTextField
                    id="wrapper"
                    labelKey="tradeAllocation.edit.wrapper"
                    value={wrapper}
                    isDisabled={isReadOnly}
                    isLoading={isLoading}
                  />
                  <LabelledTextField
                    id="tradeWay"
                    labelKey="tradeAllocation.edit.tradeWay"
                    value={tradeSide}
                    isDisabled={isReadOnly}
                    isLoading={isLoading}
                  />
                  <Currency value={currency} isDisabled={isReadOnly} isLoading={isLoading} />
                  <TradeAllocationNotional
                    notional={notional}
                    currency={currency}
                    onChange={onNotionalChange}
                    isDisabled={isReadOnly}
                    isLoading={isLoading}
                  />
                  <PercentWithUnitField
                    value={xMargin}
                    onChange={onXMarginChange}
                    id="xMargin"
                    labelKey="tradeAllocation.edit.xMargin"
                    isDisabled={isReadOnly}
                    isLoading={isLoading}
                  />
                  <PercentWithUnitField
                    value={tradingPrice}
                    onChange={onTradingPriceChange}
                    id="tradingPrice"
                    labelKey="tradeAllocation.edit.tradingPrice"
                    isDisabled={isReadOnly}
                    isLoading={isLoading}
                  />
                  <PercentWithUnitField
                    value={offerPrice}
                    onChange={onOfferPriceChange}
                    isDisabled={isReadOnly}
                    isLoading={isLoading}
                    id="offerPrice"
                    labelKey="tradeAllocation.edit.offerPrice"
                  />
                  <PercentWithUnitField
                    value={upfrontFee}
                    onChange={onUpfrontFeeChange}
                    isDisabled={isReadOnly}
                    isLoading={isLoading}
                    id="upfrontFee"
                    labelKey="tradeAllocation.edit.upfrontFee"
                  />
                  <PercentWithUnitField
                    value={sponso}
                    onChange={onSponsoChange}
                    id="sponso"
                    labelKey="tradeAllocation.edit.sponso"
                    isDisabled={isReadOnly}
                    isLoading={isLoading}
                  />
                  <PercentWithUnitField
                    value={sponsoPercentUpFront}
                    onChange={onSponsoPercentUpFrontChange}
                    id="sponsoPercentUpFront"
                    labelKey="tradeAllocation.edit.sponsoPercentUpFront"
                    isDisabled={isReadOnly}
                    isLoading={isLoading}
                  />
                  <PercentWithUnitField
                    value={f2Y}
                    onChange={onF2YChange}
                    id="f2Y"
                    labelKey="tradeAllocation.edit.f2Y"
                    isDisabled={isReadOnly}
                    isLoading={isLoading}
                  />
                  <PercentWithUnitField
                    value={topUpPercentUpFront}
                    onChange={onTopUpPercentUpFrontChange}
                    id="topUpPercentUpFront"
                    labelKey="tradeAllocation.edit.topUpPercentUpFront"
                    isDisabled={isReadOnly}
                    isLoading={isLoading}
                  />
                </div>
              </CardBody>
            </Card>
          </div>
          {Boolean(hasMonetaryPreCom) && (
            <div className="col-4 px-1">
              <Card className="h-100">
                <CardBody className="pb-4">
                  <CardTitle className="d-flex justify-content-between">
                    <FormattedMessage tagName="h6" id="tradeAllocation.monetarySchedule" />
                    <Badge isPill purpose="outline">
                      <div className="d-flex align-items-center">
                        {getPrecomStatusLabel(preComStatus)}
                        <div className={`precom-status-icon ${getPrecomStatusColor(preComStatus)}`} />
                      </div>
                    </Badge>
                  </CardTitle>
                  <div className="row row-cols-2">
                    <PercentWithUnitField
                      value={preComReferenceRate}
                      id="preComReferenceRate"
                      labelKey="tradeAllocation.monetarySchedule.preComReferenceRate"
                      isReadOnly={true}
                      isLoading={isLoading}
                    />
                    <PercentWithUnitField
                      value={preComActualizedOfferPrice}
                      id="preComActualizedOfferPrice"
                      labelKey="tradeAllocation.monetarySchedule.preComActualizedOfferPrice"
                      isReadOnly={true}
                      isLoading={isLoading}
                    />
                    <LabelledTextField
                      value={formattedPreComStartDate}
                      id="preComStartDate"
                      labelKey="tradeAllocation.monetarySchedule.preComStartDate"
                      isReadOnly={true}
                      isLoading={isLoading}
                    />
                    <LabelledTextField
                      value={formattedPreComEndDate}
                      id="preComEndDate"
                      labelKey="tradeAllocation.monetarySchedule.preComEndDate"
                      isReadOnly={true}
                      isLoading={isLoading}
                    />
                    {preComStatus === 'OPEN' && (
                      <PercentWithUnitField
                        value={preComPrice}
                        id="preComPrice"
                        labelKey="tradeAllocation.monetarySchedule.preComPrice"
                        unit={preComCurrency ?? ''}
                        isReadOnly={true}
                        isLoading={isLoading}
                        className={`price-text ${preComPrice ? 'price-border' : 'price-border-warning'}`}
                      >
                        <PrecomPrice preComPrice={preComPrice} quoteStatus={quoteStatus} />
                      </PercentWithUnitField>
                    )}
                    {preComLimitDate && (
                      <LabelledTextField
                        id="preComLimitDate"
                        labelKey="tradeAllocation.monetarySchedule.preComLimitDate"
                        value={formattedPreComLimitDate}
                        isReadOnly={true}
                        isLoading={isLoading}
                      />
                    )}
                    {traderGroup && (
                      <div className="col-12">
                        <LabelledTextField
                          id="preComTraderGroup"
                          value={traderGroup}
                          labelKey="tradeAllocation.monetarySchedule.preComTraderGroup"
                          isReadOnly={true}
                          isLoading={isLoading}
                        />
                      </div>
                    )}
                  </div>
                </CardBody>
              </Card>
            </div>
          )}
        </div>
        <div className="row py-1 flex-fill">
          <div className="col-12 px-1">
            <Card className="h-100">
              <CardBody className="py-3 overflow-x-auto">
                <CardTitle as="h6">
                  <FormattedMessage id="tradeAllocation.allocations" />
                </CardTitle>
                <TradeAllocation tabId={tabId} preComPrice={preComPrice} offerPriceUnit={preComCurrency} />
              </CardBody>
            </Card>
          </div>
        </div>
      </div>
    </FormOrientationContext.Provider>
  );
};

Component.displayName = 'TradeAllocationFormLoaded';

export const TradeAllocationMainForm = connectTradeAllocationMainForm(Component);
